import { debounce } from 'lodash';
import { createRef, useCallback, useRef, useState } from 'react';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import { v4 as uuidv4 } from 'uuid';
import { ONE_HOUR_IN_MILLISECONDS } from '../../consts/consts';
import type { Me, OrganizationSettings } from '../../types';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import {
  formatNumber4Decimals,
  GetObjectivesTreeResponse,
  OBJECTIVE_TYPE_SCOPES,
  OBJECTIVE_UNIT_TYPES,
  OBJECTIVE_VIEW_TYPES,
  ObjectiveWithRef as Objective,
  type ObjectiveViewType,
  parseObjectivesForDisplay,
  Tag,
} from '../../utils/models/Objective';
import { useEventSerializer } from '../../utils/util/EventSerializer';
import { useFeatures } from '../../utils/util/features';
import {
  EventSerializerBufferFunctionType,
  ORG_SETTING_OBJECTIVES_RESTRICT_VISIBILITY_TO_MATCH_EDITABILITY,
} from '../../utils/util/hooks';
import { renderErrorOrCallback } from '../../utils/util/util';
import { isStrategicScoped, ObjectiveType } from './ObjectiveTypeOperations';

// Picks the seed `objective_type` for a newly-created top-level objective in
// the given view. `undefined` means "no type" — the downstream
// `optimisticCollaboratorsFor` will then seed the profile owner as the sole
// collaborator (individual objective).
export const defaultObjectiveTypeIdForViewType = (
  viewType: ObjectiveViewType,
  userEditableObjectiveTypes: ObjectiveType[]
): number | undefined => {
  switch (viewType) {
    case OBJECTIVE_VIEW_TYPES.PERSON_COLLABORATING:
      return undefined;
    case OBJECTIVE_VIEW_TYPES.STRATEGIC:
      // First assignable strategic type — individual-scoped types are not
      // assignable (individual objectives must have objective_type=null).
      return userEditableObjectiveTypes.find(
        (t) => t.scope !== OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
      )?.id;
  }
};

export type ObjectiveOperation =
  | 'CASCADE_DELETE'
  | 'DELETE'
  | 'DELETE_BREAK_TREE'
  | 'DISCONNECT_FROM_PARENT'
  | 'REMOVE_FROM_COLLABORATORS'
  | 'CASCADE_REMOVE_FROM_COLLABORATORS'
  | 'IMPORT'
  | 'UPDATE';

export const useObjectiveActions = ({
  me,
  person,
  firstDay,
  lastDay,
  currentOrganization,
  defaultObjectiveTypeId,
  userEditableObjectiveTypes,
}) => {
  const [addToBufferQueue] = useEventSerializer();

  // this is to allow the user not to save to the buffer every time there's a keystroke
  const addToBufferQueueDebounced = useRef(
    debounce<EventSerializerBufferFunctionType>(addToBufferQueue, 500)
  ).current;

  // Strategic objectives are governed by type admins, so they start with no
  // collaborators; individual objectives must satisfy the ≥ 1 rule, so seed
  // with the profile owner. Mirrors the backend `SingleObjectiveSerializer.
  // create` default to keep the optimistic UI consistent with the persisted
  // state.
  const optimisticCollaboratorsFor = (
    typeId: number | null | undefined
  ): (typeof person)[] => {
    if (typeId == null) return [person];
    const type = userEditableObjectiveTypes.find((t) => t.id === typeId);
    return isStrategicScoped(type) ? [] : [person];
  };

  // Mirrors the backend's `ensure_individual_objective_has_collaborator`
  // auto-fill: when an objective's type flips to individual scope (null
  // type or any type with scope=individual) and the collaborators list is
  // empty, seed `person` so the optimistic UI matches what the server will
  // persist on save.
  const collaboratorsAfterTypeFlip = (
    value: Objective,
    newTypeId: number | null
  ): Objective['collaborators'] => {
    if ((value.collaborators?.length ?? 0) > 0) {
      return value.collaborators;
    }
    const newScope =
      newTypeId == null
        ? OBJECTIVE_TYPE_SCOPES.INDIVIDUAL
        : userEditableObjectiveTypes.find((t) => t.id === newTypeId)?.scope;
    if (newScope !== OBJECTIVE_TYPE_SCOPES.INDIVIDUAL) {
      return value.collaborators;
    }
    return [person];
  };

  // Eagerly persist the new objective so the BE applies its scope-based
  // collaborator default at create time (individual → [creator], strategic →
  // []). Without this, the first wire call is whatever the user does next —
  // and if that's a type-flip to strategic before typing a name, the BE
  // creates a strategic objective with no collaborators and it vanishes from
  // the profile on refresh. Mirrors the addChild / addSibling pattern.
  const createNewTopItem = ({
    setObjectives,
  }: {
    setObjectives: (callback: (objectives: Objective[]) => Objective[]) => void;
  }) => {
    const newObj = {
      name: '',
      key: uuidv4(),
      coverage_start_date: firstDay,
      coverage_end_date: lastDay,
      organization: currentOrganization,
      owner_person: person,
      collaborators: optimisticCollaboratorsFor(defaultObjectiveTypeId),
      children: [],
      ref: createRef<HTMLTextAreaElement>(),
      isNew: true,
      auto_calculation: false,
      unit_type: OBJECTIVE_UNIT_TYPES.NUMBER,
      tags: [],
      objective_type: defaultObjectiveTypeId ?? null,
    };
    // @ts-expect-error
    setObjectives((objectives: Objective[]) => [...objectives, newObj]);
    // @ts-expect-error
    addToBufferQueue(toApiObjective(newObj, null));
  };

  const calculateDeleteOperation = (
    value: Objective,
    { deleteAllChildren, removeForAllCollaborators }
  ): ObjectiveOperation => {
    if (removeForAllCollaborators) {
      if (deleteAllChildren) {
        return 'CASCADE_DELETE';
      }

      if (value.children && value.children.length > 0) {
        return 'DELETE_BREAK_TREE';
      }

      return 'DELETE';
    }

    // if not delete for all collaborators
    if (deleteAllChildren) {
      return 'CASCADE_REMOVE_FROM_COLLABORATORS';
    }
    return 'REMOVE_FROM_COLLABORATORS';
  };

  // This event is triggered when the user delete an objective
  const deleteObjective = ({
    value,
    parentObjective = null,
    updateParentObjective,
    deleteAllChildren = false,
    removeForAllCollaborators = false,
  }: {
    value: Objective;
    parentObjective?: Objective | null;
    updateParentObjective: (value: Objective) => void;
    deleteAllChildren?: boolean;
    removeForAllCollaborators?: boolean;
  }) => {
    addToBufferQueueDebounced({
      ...toApiObjective(value, parentObjective),
      collaborators: value.collaborators,
      operation: calculateDeleteOperation(value, {
        deleteAllChildren,
        removeForAllCollaborators,
      }),
    });

    // remove the objective from the parent on the UI
    if (parentObjective) {
      updateParentObjective({
        ...parentObjective,
        children:
          parentObjective.children?.filter((child) => child.key != value.key) ??
          [],
      });
    } else {
      // remove the objective from the top level on the UI
      updateParentObjective(value);
    }
  };

  // This event is triggered when the user add a child to an objective
  const addChild = ({
    value,
    setValue,
    setShowChildren,
  }: {
    value: Objective;
    setValue: (value: Objective) => void;
    setShowChildren: (value: boolean) => void;
  }) => {
    const childType = computeDefaultType(value, userEditableObjectiveTypes);

    const newChild = {
      key: uuidv4(),
      name: '',
      parent: value.id,
      parent_key: value.key,
      coverage_start_date: value.coverage_start_date,
      coverage_end_date: value.coverage_end_date,
      organization: currentOrganization,
      owner_person: person,
      collaborators: optimisticCollaboratorsFor(childType),
      auto_calculation: false,
      unit_type: OBJECTIVE_UNIT_TYPES.NUMBER,
      ref: createRef(),
      isNew: true,
      tags: [],
      objective_type: childType,
    };
    setValue({
      ...value,
      // @ts-expect-error
      children: [...(value?.children ?? []), newChild],
    });

    addToBufferQueue(
      toApiObjective(
        // @ts-expect-error
        newChild,
        value
      )
    );
    setShowChildren(true);
  };

  // This event is triggered when the user add a child to the parent of the current objective
  const addSibling = ({
    value,
    parentObjective,
    person,
    me,
    updateParentObjective,
  }) => {
    const newChild = {
      key: uuidv4(),
      name: '',
      coverage_start_date: value.coverage_start_date,
      coverage_end_date: value.coverage_end_date,
      organization: currentOrganization,
      owner_person: person,
      collaborators: optimisticCollaboratorsFor(value?.objective_type),
      auto_calculation: false,
      unit_type: OBJECTIVE_UNIT_TYPES.NUMBER,
      ref: createRef(),
      isNew: true,
      tags: [],
      objective_type: value?.objective_type ?? null,
    };
    addToBufferQueue(
      toApiObjective(
        // @ts-expect-error
        newChild,
        parentObjective
      )
    );

    updateParentObjective({
      ...parentObjective,
      children: [...(parentObjective?.children || []), newChild],
    });
  };

  const updateRelatedObjectives = ({
    value,
    setValue,
    relatedObjectives,
    parentObjective,
  }) => {
    const modified = {
      ...value,
      related_to: relatedObjectives,
    };
    addToBufferQueueDebounced(toApiObjective(modified, parentObjective));
    setValue(modified);
  };

  const updateRelatedObjectivesRoot = ({
    value,
    setValue,
    relatedObjectives,
  }) => {
    const modified = {
      ...value,
      related_to: relatedObjectives,
    };
    addToBufferQueueDebounced(toApiObjective(modified, value.parent_objective));
    setValue(modified);
  };

  // Disconnects a node in the middle of the tree from its parent and reloads
  // the tree so the topology change is reflected.
  const disconnectFromParentObjective = ({ value }: { value: Objective }) => {
    addToBufferQueue({
      ...toApiObjective(value, null),
      operation: 'DISCONNECT_FROM_PARENT',
    });
  };

  // This event is triggered when the user change link to the parent objective on a top level
  const setRootParentObjective = ({
    rootParentObjectives,
    value,
    setValue,
  }: {
    rootParentObjectives: Objective[];
    value: Objective;
    setValue: (value: Objective) => void;
  }) => {
    const rootParentObjective =
      rootParentObjectives.length > 0 ? rootParentObjectives[0] : null;

    addToBufferQueueDebounced(toApiObjective(value, rootParentObjective));
    setValue({
      ...value,
      parent_objective: rootParentObjective,
    });
  };

  // this event is triggered when the user change some indicator of the objectives
  const updateIndicators = ({
    updatedObj,
    value,
    setValue,
    parentObjective,
    updateParentObjective,
  }) => {
    const updated = fromIndicators(value, updatedObj);
    addToBufferQueue(toApiObjective(updated, parentObjective));
    setValue(updated);
    updateParentObjective({
      ...parentObjective,
      children: parentObjective.children.map((child) =>
        child.key !== value.key ? child : updated
      ),
    });
  };

  // this event is triggered when the user change some indicator of the objectives on top
  const updateIndicatorRoot = ({ updatedObj, value, setValue }) => {
    const updated = fromIndicators(value, updatedObj);
    addToBufferQueueDebounced(toApiObjective(updated, value.parent_objective));
    setValue(updated);
  };

  // This event is triggered when the user change the text of the objective
  const updateText = ({
    e,
    value,
    parentObjective,
    setValue,
  }: {
    e: React.ChangeEvent<HTMLTextAreaElement>;
    value: Objective;
    parentObjective: Objective;
    setValue: (value: Objective) => void;
  }) => {
    addToBufferQueueDebounced(
      toApiObjective({ ...value, name: e.target.value }, parentObjective)
    );
    setValue({ ...value, name: e.target.value });
  };

  // This event is triggered when the user change the text of the objective on top
  const updateTextRoot = ({
    e,
    value,
    setValue,
  }: {
    e: React.ChangeEvent<HTMLTextAreaElement>;
    value: Objective;
    setValue: (value: Objective) => void;
  }) => {
    addToBufferQueueDebounced(
      toApiObjective(
        { ...value, name: e.target.value },
        value?.parent_objective ?? null
      )
    );
    setValue({ ...value, name: e.target.value });
  };

  const updateTagsRoot = ({
    tags,
    value,
    setValue,
  }: {
    tags: Tag[];
    value: Objective;
    setValue: (value: Objective) => void;
  }) => {
    const modified = { ...value, tags };
    addToBufferQueueDebounced(
      toApiObjective(modified, value?.parent_objective ?? null)
    );
    setValue(modified);
  };

  const updateTags = ({
    tags,
    value,
    parentObjective,
    setValue,
  }: {
    tags: Tag[];
    value: Objective;
    parentObjective: Objective;
    setValue: (value: Objective) => void;
  }) => {
    const modified = { ...value, tags };
    addToBufferQueueDebounced(toApiObjective(modified, parentObjective));
    setValue(modified);
  };

  const updateObjectiveTypeRoot = ({
    objectiveType,
    value,
    setValue,
  }: {
    objectiveType: number | null;
    value: Objective;
    setValue: (value: Objective) => void;
  }) => {
    const modified = {
      ...value,
      objective_type: objectiveType,
      collaborators: collaboratorsAfterTypeFlip(value, objectiveType),
    };
    addToBufferQueueDebounced(
      toApiObjective(modified, value?.parent_objective ?? null)
    );
    setValue(modified);
  };

  const updateObjectiveType = ({
    objectiveType,
    value,
    parentObjective,
    setValue,
    updateParentObjective,
  }: {
    objectiveType: number | null;
    value: Objective;
    parentObjective: Objective;
    setValue: (value: Objective) => void;
    updateParentObjective: (value: Objective) => void;
  }) => {
    const modified = {
      ...value,
      objective_type: objectiveType,
      collaborators: collaboratorsAfterTypeFlip(value, objectiveType),
    };
    addToBufferQueueDebounced(toApiObjective(modified, parentObjective));
    setValue(modified);
    updateParentObjective({
      ...parentObjective,
      children:
        parentObjective.children?.map((child) =>
          child.key !== value.key ? child : modified
        ) ?? [],
    });
  };

  const updateTextKeyDownRoot = ({
    e,
    value,
    setShowDeleteModal,
    updateParentObjective,
    setValue,
    setShowChildren,
  }) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if ((value?.children?.length ?? 0) === 0) {
        addChild({ value, setValue, setShowChildren });
      } else {
        setShowChildren(true);
        const firstChildRef = value.children[0].ref.current;
        firstChildRef?.focus();
        firstChildRef?.setSelectionRange(
          firstChildRef.value.length,
          firstChildRef.value.length
        );
      }
    }
    if (e.keyCode === 8 && (e.target?.value === '' || !e.target?.value)) {
      e.preventDefault();
      if ((value?.children?.length ?? 0) > 0) {
        setShowDeleteModal(true);
      } else {
        deleteObjective({
          value,
          updateParentObjective,
        });

        // updateParentObjective(value);
      }
      return;
    }
  };

  const isLastSibling = (objective: Objective, parentObjective: Objective) => {
    const siblings = parentObjective?.children || [];
    const siblingsCount = siblings.length;
    return (
      siblingsCount === 0 || siblings[siblingsCount - 1].key == objective.key
    );
  };

  const updateTextKeyDown = ({
    e,
    addSibling,
    value,
    parentObjective,
    setShowDeleteModal,
    deleteObjective,
    updateParentObjective,
  }) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isLastSibling(value, parentObjective)) {
        addSibling({
          value,
          parentObjective,
          person,
          me,
          updateParentObjective,
        });
      } else {
        const siblings = parentObjective?.children || [];
        const currentIndex = siblings.findIndex(
          (sibling) => sibling.key === value.key
        );
        const nextSiblingRef = siblings[currentIndex + 1].ref.current;
        nextSiblingRef.focus();
        nextSiblingRef.setSelectionRange(
          nextSiblingRef.value.length,
          nextSiblingRef.value.length
        );
      }
    }
    if (e.keyCode === 8 && (e.target?.value === '' || !e.target?.value)) {
      e.preventDefault();
      if (value.children && value.children.length > 0) {
        setShowDeleteModal(true);
        return;
      }
      deleteObjective({
        value,
        parentObjective,
        updateParentObjective,
      });
      return;
    }
  };

  return {
    createNewTopItem,
    deleteObjective,
    disconnectFromParentObjective,
    addChild,
    addSibling,
    updateRelatedObjectives,
    updateRelatedObjectivesRoot,
    setRootParentObjective,
    updateIndicatorRoot,
    updateIndicators,
    updateText,
    updateTextRoot,
    updateTagsRoot,
    updateTags,
    updateObjectiveTypeRoot,
    updateObjectiveType,
    updateTextKeyDownRoot,
    updateTextKeyDown,
  };
};

export const OBJECTIVE_SAVE_MAX_RETRY_ATTEMPTS = 5;

// A 4xx is a validation/permission rejection: sending the same payload again
// fails the same way. Network errors (no status), 429 and 5xx may pass later.
export const isRetryableObjectiveSaveError = (error): boolean => {
  const status = error?.status;
  return status == null || status === 429 || status >= 500;
};

export const useObjectiveOperations = ({
  isDemoOrPreviewMode,
  currentOrganization,
  currentProxyPerson,
  person,
  firstDay,
  lastDay,
  setObjectives,
  setState,
  viewType,
}) => {
  const proxyParams = useProxyParameters();

  const onSubmit = (objective, onSuccess, onError) => {
    if (isDemoOrPreviewMode) {
      // just return the objective without saving it
      onSuccess(objective);
      return;
    }

    const transformedObjective = transformObjectiveForSubmit(objective);
    switch (objective.operation as ObjectiveOperation) {
      case 'DELETE': {
        ConfirmAPI.sendRequestToConfirm(
          'DELETE',
          `/v2/objectives/${transformedObjective.key}`,
          { ...proxyParams },
          renderErrorOrCallback(onSuccess, onError)
        );
        break;
      }
      case 'DELETE_BREAK_TREE': {
        ConfirmAPI.sendRequestToConfirm(
          'DELETE',
          `/v2/objectives/${transformedObjective.key}`,
          { ...proxyParams },
          renderErrorOrCallback(onSuccess, onError)
        );
        break;
      }
      case 'CASCADE_DELETE': {
        ConfirmAPI.sendRequestToConfirm(
          'DELETE',
          `/v2/objectives/${transformedObjective.key}`,
          { ...proxyParams, cascade: true },
          renderErrorOrCallback(onSuccess, onError)
        );
        break;
      }

      case 'REMOVE_FROM_COLLABORATORS': {
        console.log(
          'Removing self from collaborators',
          transformedObjective.key
        );
        ConfirmAPI.sendRequestToConfirm(
          'POST',
          `/objectives/${objective.key}/collaborators`,
          {
            ...proxyParams,
            collaborators: objective.collaborators
              .map((it) => it.id)
              .filter((it) => it !== person.id),
          },
          renderErrorOrCallback(onSuccess, onError),
          null,
          null
        );
        break;
      }

      case 'CASCADE_REMOVE_FROM_COLLABORATORS': {
        console.log(
          'Removing self from collaborators on all the sub-objectives',
          transformedObjective.key
        );
        ConfirmAPI.sendRequestToConfirm(
          'POST',
          `/objectives/${objective.key}/collaborators`,
          {
            ...proxyParams,
            cascade: true,
            collaborators: [person.id],
          },
          renderErrorOrCallback(onSuccess, onError),
          null,
          null
        );
        break;
      }

      case 'IMPORT': {
        console.log(
          'Import objective amending collaborators',
          transformedObjective.key
        );
        ConfirmAPI.sendRequestToConfirm(
          'POST',
          `/objectives/${objective.key}/collaborators`,
          {
            ...proxyParams,
            collaborators: objective.collaborators.map((it) => it.id),
          },
          renderErrorOrCallback(onSuccess, onError),
          null,
          null
        );
        break;
      }
      case 'DISCONNECT_FROM_PARENT': {
        ConfirmAPI.sendRequestToConfirm(
          'POST',
          '/v2/objectives',
          { ...proxyParams, ...transformedObjective },
          renderErrorOrCallback(onSuccess, onError)
        );
        break;
      }
      default: {
        ConfirmAPI.sendRequestToConfirm(
          'POST',
          '/v2/objectives',
          { ...proxyParams, ...transformedObjective },
          renderErrorOrCallback(onSuccess, onError)
        );
      }
    }
  };

  const onSuccess = (successItem) => {
    switch (successItem.operation as ObjectiveOperation) {
      case 'IMPORT':
      case 'REMOVE_FROM_COLLABORATORS':
      case 'CASCADE_REMOVE_FROM_COLLABORATORS':
      case 'DELETE_BREAK_TREE':
      case 'DISCONNECT_FROM_PARENT': {
        console.log('Refresh :', successItem);
        setState('LOADING');
        getAllTreeObjectives({
          person,
          firstDay,
          lastDay,
          shouldCacheObjectivesOnFrontend: false,
          user: null,
          currentOrganization,
          currentProxyPerson,
          viewType,
          onFetchedObjectives: (data) => {
            setObjectives(data);
            setState('FETCHED');
          },
        });
        break;
      }
    }
    console.info('Successfully saved objective:', successItem);
  };

  const onError = (message, errorItem) => {
    console.error(message);
    console.warn('Could not save agenda item, retrying...:', errorItem.key);
  };

  return { onSubmit, onSuccess, onError };
};

export const getAllTreeObjectives = ({
  person,
  currentOrganization,
  currentProxyPerson,
  firstDay,
  lastDay,
  shouldCacheObjectivesOnFrontend,
  user,
  onFetchedObjectives,
  viewType,
}) => {
  const params = {
    person: person.id,
    organization: currentOrganization?.id,
    proxy: currentProxyPerson ? currentProxyPerson.email : undefined,
    coverage_start_date: firstDay,
    coverage_end_date: lastDay,
    view_type: viewType,
  };

  const fetchOrgSettings = new Promise((resolve, reject) => {
    ConfirmAPI.getUrlWithCache(
      '/organization-settings',
      'organization-settings',
      user?.sub,
      currentProxyPerson,
      {
        names: [
          ORG_SETTING_OBJECTIVES_RESTRICT_VISIBILITY_TO_MATCH_EDITABILITY,
        ],
        organization: currentOrganization.id,
      },
      resolve,
      reject,
      ONE_HOUR_IN_MILLISECONDS
    );
  });

  const onError = (message) => {
    if (message.status === 403) {
      // zero objectives matched with visibility criteria will return the correct empty page
      onFetchedObjectives([], false, [], []);
    } else {
      console.log(
        'Error Fetching objective tree for person with id ',
        person.id,
        message
      );
    }
  };

  fetchOrgSettings
    .then((orgSettings: unknown) => {
      const companyOKRsFullVisibility = !(
        (orgSettings as OrganizationSettings)
          ?.objectives_restrict_visibility_to_match_editability ?? false
      );
      const onTreeFetchSuccess = (data: GetObjectivesTreeResponse) => {
        if (!data?.results) {
          onFetchedObjectives([], companyOKRsFullVisibility, []);
          return;
        }

        const editableObjectiveIds = data.editable_objective_ids ?? [];
        const objectivesWithRefs: Objective[] = data.results.map(
          (objective) => parseObjectivesForDisplay(objective) // can do here the related to game:
        );

        onFetchedObjectives(
          objectivesWithRefs,
          companyOKRsFullVisibility,
          editableObjectiveIds
        );
      };

      ConfirmAPI.getUrlWithCache(
        '/objectives/tree',
        shouldCacheObjectivesOnFrontend && '/objectives/tree',
        shouldCacheObjectivesOnFrontend && user?.sub,
        currentProxyPerson,
        params,
        onTreeFetchSuccess,
        onError
      );
    })
    .catch(onError);
};

interface RelatedToObjectivePost {
  id: number;
  key?: string;
  coverage_start_date: string;
  coverage_end_date: string;
}

export interface ApiObjectivePost {
  id: number;
  key?: string;
  coverage_start_date: string;
  coverage_end_date: string;
  due_date: string;
  name: string;
  score: number | null;
  score_denominator: number | null;
  weight: number | null;
  auto_calculation: boolean;
  unit_type: string;
  currency_code?: string | null;
  score_comments?: string;
  score_comments_author_person?: Me;
  status: string;
  parent?: number | null;
  parent_key?: string | null;
  related_to?: RelatedToObjectivePost[];
  organization?: number;
  owner_person: number;
  tags?: Tag[];
  objective_type?: number | null;
}

const toApiObjective = (
  {
    id,
    key,
    coverage_start_date,
    coverage_end_date,
    due_date,
    name,
    score,
    score_denominator,
    weight,
    auto_calculation,
    unit_type,
    currency_code,
    score_comments,
    score_comments_author_person,
    status,
    related_to,
    organization,
    owner_person,
    tags,
    objective_type,
  }: Objective,
  parentObjective: Objective | null
): ApiObjectivePost => {
  return {
    id,
    key,
    coverage_start_date,
    coverage_end_date,
    due_date,
    name,
    score,
    score_denominator,
    weight,
    auto_calculation,
    unit_type,
    // Only include currency_code when unit_type is CURRENCY
    currency_code:
      unit_type === OBJECTIVE_UNIT_TYPES.CURRENCY ? currency_code : null,
    score_comments,
    score_comments_author_person,
    status,
    organization: organization?.id,
    owner_person: owner_person?.id,
    parent: parentObjective?.id ?? null,
    parent_key: parentObjective?.key ?? null,
    related_to: related_to?.map((it) => ({
      id: it.id,
      key: it.key,
      coverage_start_date: it.coverage_start_date,
      coverage_end_date: it.coverage_end_date,
    })),
    tags,
    objective_type,
  };
};

// do not need the children since we are not pushing them
const transformObjectiveForSubmit = (o: Objective) => {
  return {
    ...o,
    score: o.score ? formatNumber4Decimals(o.score / 100) : null,
    score_denominator: o.score_denominator
      ? formatNumber4Decimals(o.score_denominator / 100)
      : null,
    weight: o.weight ? formatNumber4Decimals(o.weight / 100) : null,
    score_comments_author_person: o.score_comments_author_person?.id,
  };
};

/**
 * Compute the default objective_type for a new child.
 * Inherit from the last sibling, or find a type whose link_parent_types
 * includes the parent's type. Individual-scoped types are not assignable
 * (the API requires objective_type=null), so they never match.
 */
function computeDefaultType(
  parent: Objective,
  userEditableObjectiveTypes: ObjectiveType[]
): number | null {
  const siblings = parent?.children ?? [];
  const lastSibling =
    siblings.length > 0 ? siblings[siblings.length - 1] : null;

  if (lastSibling) {
    return lastSibling.objective_type ?? null;
  }

  if (parent.objective_type && userEditableObjectiveTypes.length > 0) {
    const nextType = userEditableObjectiveTypes.find(
      (t) =>
        t.scope !== OBJECTIVE_TYPE_SCOPES.INDIVIDUAL &&
        t.link_parent_types &&
        t.link_parent_types.includes(parent.objective_type!)
    );
    return nextType?.id ?? null;
  }

  return null;
}

function fromIndicators(value, updatedObj) {
  return {
    ...value,
    score: updatedObj.score,
    score_denominator: updatedObj.score_denominator,
    weight: updatedObj.weight,
    auto_calculation: updatedObj.auto_calculation,
    unit_type: updatedObj.unit_type,
    currency_code: updatedObj.currency_code,
    status: updatedObj.status,
    coverage_start_date: updatedObj.coverage_start_date,
    coverage_end_date: updatedObj.coverage_end_date,
    score_comments: updatedObj.score_comments,
    score_comments_author_person: updatedObj.score_comments_author_person,
  };
}

export const hasNoUsefulInformation = (value: Objective): boolean => {
  const hasNoChildren = (value.children?.length ?? 0) === 0;
  const isEmpty = !value.name;
  const allChildrenHaveNoUsefulInformation =
    value.children?.every((it) => hasNoUsefulInformation(it)) ?? true;
  return isEmpty && (hasNoChildren || allChildrenHaveNoUsefulInformation);
};

type ObjectiveCopyToNextPeriodParams = {
  key: string;
  personId?: number;
};

export const useObjectiveCopyToNextPeriod = ({
  key,
  personId,
}: ObjectiveCopyToNextPeriodParams) => {
  const [disabled, setDisabled] = useState<boolean>(true);
  const [copyAllChildren, setCopyAllChildren] = useState<boolean>(false);

  const action = useCallback(({ copyAllChildren }) => {
    setCopyAllChildren(copyAllChildren);
    setDisabled(false);
  }, []);

  const reset = useCallback(() => {
    setDisabled(true);
    setCopyAllChildren(false);
  }, []);

  const callback = useCallback(() => {}, []);

  const { status, error } = useConfirmApi<void>({
    method: 'POST',
    url: `/objectives/${key}/copy-to-next-period`,
    params: { key, must_copy_children: copyAllChildren, person: personId },
    callback,
    disabled,
  });

  return {
    action,
    reset,
    status,
    error,
  };
};

export const useObjectiveAddTag = () => {
  const [isAddingTag, setIsAddingTag] = useState(false);
  const { isOptionEnabled } = useFeatures();
  const tagsEnabled = isOptionEnabled('objectives', 'tags_enabled');
  const startAddingTag = useCallback(() => setIsAddingTag(true), []);
  return {
    isAddingTag,
    setIsAddingTag,
    onClickAddTag: tagsEnabled ? startAddingTag : undefined,
  };
};
